import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Table, Input, Button, Space, Spin } from "antd";
import { message } from "../../components/antdMessageBridge";
import { SearchOutlined } from "@ant-design/icons";
import CommonObj from "../../CommonObj";
import { useNavigate } from "react-router-dom";
import { useAuthToken } from "../../components/usecontext/AuthContext";
import { exportToExcel } from '../exporttoexcel/exporttoexcel';
import { runGridExport } from "../../components/gridExportHelper";
import { encryptTimestamp } from "../../components/commoncsrf/csrf-common";
import { renderCommonPaginationTotal } from "../../components/commonpagination/common-pagination";
import "../../components/commonpagination/common-pagination.css";
import { buildTimestampedFileName } from "../../components/cstDateFileName";
const Cancelqueue = () => {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sortedInfo, setSortedInfo] = useState({});
    const [searchFilters, setsearchFilters] = useState({});
    const { accessToken } = useAuthToken();
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [isArchive, setIsArchive] = useState(false);
    const searchInput = useRef(null);

    const handleReset = (dataIndex, confirm, clearFilters) => {
        clearFilters();
        setsearchFilters((prev) => {
            const updated = { ...prev };
            delete updated[dataIndex];
            return updated;
        });
        fetchData({
            pagination,
            sorter: sortedInfo,
            filters: { ...searchFilters, [dataIndex]: undefined },
            archiveStatus: isArchive
        });
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    style={{ marginBottom: 8, display: "block" }}
                    onPressEnter={() => confirm()}
                />
                <Space>
                    <Button type="primary" size="small" onClick={() => confirm()}>Search</Button>
                    <Button size="small" onClick={() => {
                        handleReset(dataIndex, confirm, clearFilters);
                        confirm();
                    }}>Reset</Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />),
        filteredValue: searchFilters[dataIndex] ? [searchFilters[dataIndex]] : null,
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        sorter: false,
        sortOrder: sortedInfo.columnKey === dataIndex ? sortedInfo.order : null,
    });

    const columns = [
        {
            title: "BID No",
            dataIndex: "bid",
            key: "bid",
            width: 160,
            sorter: (a, b) => a.bid.localeCompare(b.bid),
            ...getColumnSearchProps("bid"),
        },
        {
            title: "ISBN",
            dataIndex: "isbn",
            key: "isbn",
            width: 200,
            sorter: (a, b) => a.isbn.localeCompare(b.isbn),
            ...getColumnSearchProps("isbn"),
        },
        {
            title: "Title",
            dataIndex: "title",
            key: "title",
            width: 220,
            sorter: (a, b) => a.title.localeCompare(b.title),
            ...getColumnSearchProps("title"),
        },
        {
            title: "Author",
            dataIndex: "author",
            key: "author",
            width: 220,
            sorter: (a, b) => a.author.localeCompare(b.author),
            ...getColumnSearchProps("author"),
        },
        {
            title: "Publisher",
            dataIndex: "publisher",
            key: "publisher",
            width: 220,
            sorter: (a, b) => a.publisher.localeCompare(b.publisher),
            ...getColumnSearchProps("publisher"),
        },
        {
            title: "Language",
            dataIndex: "language",
            key: "language",
            width: 160,
        },
        {
            title: "Book Format",
            dataIndex: "format",
            key: "format",
            width: 200,
            filters: [
                { text: "Hardcover", value: "Hardcover" },
                { text: "Paperback", value: "Paperback" },
                { text: "eBook", value: "eBook" },
            ],
            onFilter: (value, record) => record.format === value,
            sorter: (a, b) => a.format.localeCompare(b.format),
            ...getColumnSearchProps("format"),
        },
        {
            title: "Condition",
            dataIndex: "condition",
            key: "condition",
            width: 120,
            ...getColumnSearchProps("condition"),
        },
        {
            title: "Rack No",
            dataIndex: "rackShelf",
            key: "rackShelf",
            width: 200,
            align: "center",
            render: (text) => <span style={{ whiteSpace: 'pre' }}>{text}</span>,
            ...getColumnSearchProps("rackShelf"),
        },
        {
            title: "Content",
            dataIndex: "content",
            key: "content",
            width: 120,
        },
        {
            title: "Vendor",
            dataIndex: "vendorName",
            key: "vendorName",
            width: 200,
            ...getColumnSearchProps("vendorName"),
        },
        {
            title: "Status",
            dataIndex: "statusText",
            key: "statusText",
            width: 200,
            sorter: (a, b) => a.statusText.localeCompare(b.statusText),
            ...getColumnSearchProps("statusText"),
            render: (_, record) => (
                <Space size="middle" className="grid-badge">
                    <span>
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <g clipPath="url(#clip0_875_15026)">
                                <path
                                    d="M10.2621 2.70235L9.86074 3.14833V3.14833L10.2621 2.70235ZM12.9013 5.07761L12.4999 5.52359L12.9013 5.07761ZM14.4362 6.76932L13.8881 7.01342V7.01342L14.4362 6.76932ZM2.11454 13.8855L2.53881 13.4613H2.53881L2.11454 13.8855ZM13.8858 13.8855L13.4615 13.4613L13.8858 13.8855ZM9.3335 14.6666V14.0666H6.66683V14.6666V15.2666H9.3335V14.6666ZM1.3335 9.33325H1.9335V6.66658H1.3335H0.733496V9.33325H1.3335ZM14.6668 9.04185H14.0668V9.33325H14.6668H15.2668V9.04185H14.6668ZM10.2621 2.70235L9.86074 3.14833L12.4999 5.52359L12.9013 5.07761L13.3027 4.63164L10.6635 2.25637L10.2621 2.70235ZM14.6668 9.04185H15.2668C15.2668 7.92511 15.2789 7.18687 14.9843 6.52521L14.4362 6.76932L13.8881 7.01342C14.0547 7.38762 14.0668 7.81912 14.0668 9.04185H14.6668ZM12.9013 5.07761L12.4999 5.52359C13.4088 6.34156 13.7214 6.63922 13.8881 7.01342L14.4362 6.76932L14.9843 6.52521C14.6896 5.86355 14.1328 5.3787 13.3027 4.63164L12.9013 5.07761ZM6.68669 1.33325V1.93325C7.74879 1.93325 8.12466 1.94252 8.45802 2.07044L8.67298 1.51026L8.88794 0.950092C8.29872 0.723989 7.65792 0.733252 6.68669 0.733252V1.33325ZM10.2621 2.70235L10.6635 2.25637C9.94509 1.6098 9.47707 1.17616 8.88794 0.950092L8.67298 1.51026L8.45802 2.07044C8.79146 2.19839 9.07535 2.44147 9.86074 3.14833L10.2621 2.70235ZM6.66683 14.6666V14.0666C5.39279 14.0666 4.49762 14.0653 3.82072 13.9743C3.16108 13.8856 2.79927 13.7217 2.53881 13.4613L2.11454 13.8855L1.69028 14.3098C2.21087 14.8304 2.86812 15.057 3.66082 15.1636C4.43627 15.2679 5.42671 15.2666 6.66683 15.2666V14.6666ZM1.3335 9.33325H0.733496C0.733496 10.5734 0.732222 11.5638 0.836477 12.3393C0.943054 13.132 1.16969 13.7892 1.69028 14.3098L2.11454 13.8855L2.53881 13.4613C2.27835 13.2008 2.11446 12.839 2.02578 12.1794C1.93477 11.5025 1.9335 10.6073 1.9335 9.33325H1.3335ZM9.3335 14.6666V15.2666C10.5736 15.2666 11.5641 15.2679 12.3395 15.1636C13.1322 15.057 13.7895 14.8304 14.31 14.3098L13.8858 13.8855L13.4615 13.4613C13.2011 13.7217 12.8392 13.8856 12.1796 13.9743C11.5027 14.0653 10.6075 14.0666 9.3335 14.0666V14.6666ZM14.6668 9.33325H14.0668C14.0668 10.6073 14.0656 11.5025 13.9745 12.1794C13.8859 12.839 13.722 13.2008 13.4615 13.4613L13.8858 13.8855L14.31 14.3098C14.8306 13.7892 15.0573 13.132 15.1638 12.3393C15.2681 11.5638 15.2668 10.5734 15.2668 9.33325H14.6668ZM1.3335 6.66658H1.9335C1.9335 5.39254 1.93477 4.49737 2.02578 3.82048C2.11446 3.16084 2.27835 2.79903 2.53881 2.53856L2.11454 2.1143L1.69028 1.69004C1.16969 2.21062 0.943054 2.86787 0.836477 3.66058C0.732222 4.43602 0.733496 5.42647 0.733496 6.66658H1.3335ZM6.68669 1.33325V0.733252C5.4399 0.733252 4.44468 0.731988 3.66614 0.836186C2.8708 0.942632 2.21133 1.16898 1.69028 1.69004L2.11454 2.1143L2.53881 2.53856C2.7988 2.27857 3.16171 2.1144 3.82533 2.02558C4.50575 1.93452 5.40609 1.93325 6.68669 1.93325V1.33325Z"
                                    fill="#000579"
                                />
                                <path
                                    opacity="0.5"
                                    d="M8.6665 1.66675V3.33341C8.6665 4.90476 8.6665 5.69044 9.15466 6.17859C9.64281 6.66675 10.4285 6.66675 11.9998 6.66675H14.6665"
                                    stroke="#000579"
                                    strokeWidth="1.2"
                                />
                                <path
                                    opacity="0.5"
                                    d="M4.00016 10.6667L7.3335 10.6667M7.3335 10.6667L6.0835 9.33333M7.3335 10.6667L6.0835 12"
                                    stroke="#000579"
                                    strokeWidth="1.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </g>
                            <defs>
                                <clipPath id="clip0_875_15026">
                                    <rect width="16" height="16" fill="white" />
                                </clipPath>
                            </defs>
                        </svg>
                    </span>
                    <span>{record.statusText}</span>
                </Space>
            ),
        },
    ];

    const getRowClassName = (record, index) => {
        return index % 2 === 0 ? "odd-row" : "even-row";
    };

    const handleTableChange = (newPagination, filters, sorter) => {
        const normalizeFilters = {};
        Object.keys(filters).forEach((key) => {
            if (filters[key] && filters[key].length > 0) {
                normalizeFilters[key] = filters[key][0];
            }
        });

        setsearchFilters(normalizeFilters);
        setSortedInfo(sorter);

        setPagination((prev) => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        }));

        fetchData({
            pagination: {
                current: newPagination.current,
                pageSize: newPagination.pageSize,
            },
            sorter,
            filters: normalizeFilters,
            archiveStatus: isArchive
        });
    };

    const handleNavigation = (route) => {
        navigate(route);
    };

    const fetchData = async ({ pagination: pag, filters, sorter, archiveStatus = isArchive }) => {
        const payload = {
            pageNo: pag?.current,
            pageSize: pag?.pageSize || 10,
            search: JSON.stringify(filters),
            orderByColumn: sorter?.field || "CreatedOn",
            orderBy: sorter?.order === "ascend" ? "ASC" : "DESC",
            isArchive: archiveStatus
        };
        setLoading(true);
        try {
            const myHeaders = {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp(),
            };
            const response = await fetch(CommonObj.Endpoints.GetBookDetails, {
                method: "POST",
                headers: myHeaders,
                body: JSON.stringify(payload),
            });
            const responseText = await response?.text();
            let result;

            if (!response.ok) {
                if (responseText?.includes("No books found.")) {
                    result = [];
                } else {
                    throw new Error(responseText || "Network response was not ok");
                }
            }

            try {
                result = JSON.parse(responseText);
                result = responseText.includes("No books found.") ? [] : result;
            } catch (e) {
                if (responseText.includes("No books found.")) {
                    result = [];
                } else {
                    throw new Error(responseText || "Failed to parse API response.");
                }
            }
            const books = Array.isArray(result) ? result : [result];
            const formattedData = books.map((item) => ({
                ...item,
                key: item.referenceId,
            }));
            setData(formattedData);
            const total = formattedData[0]?.totalRows || 0;

            setPagination((prev) => ({
                ...prev,
                current: pag?.current || prev.current,
                pageSize: pag?.pageSize || prev.pageSize,
                total,
            }));
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData({ pagination, sorter: sortedInfo, filters: searchFilters, archiveStatus: isArchive });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleExport = async () => {
        const requestData = {
            pageNo: 1,
            pageSize: 0,
            search: JSON.stringify(searchFilters),
            orderByColumn: "CreatedOn",
            orderBy: "DESC",
            isArchive: isArchive,
        };

        const myHeaders = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
            "X-CSRF-Token": encryptTimestamp(),
        };

        setLoading(true);
        await runGridExport({
            request: fetch(CommonObj.Endpoints.GetBookDetails, {
                method: "POST",
                headers: myHeaders,
                body: JSON.stringify(requestData),
            }),
            exportFn: exportToExcel,
            noDataMarkers: ["No books found."],
            transform: (books) =>
                books.map(item => ({
                    ...item,
                    isActive: item.isActive === true ? 'Active' : 'InActive'
                })),
            columns: columns.filter(person => person.key !== "actions"),
            filename: buildTimestampedFileName("ActiveQueue"),
            onSettled: () => setLoading(false),
        });
    };

    const handleArchiveChange = (e) => {
        const newArchiveStatus = e.target.checked;
        setIsArchive(newArchiveStatus);
        fetchData({
            pagination, sorter: sortedInfo, filters: searchFilters,
            archiveStatus: newArchiveStatus
        });
    };

    if (loading) {
        return (
            <Spin size="large" style={{ margin: "20px auto" }} />
        );
    }

    return (
        <div className="active-page-container">
            <div className="active-page-header-container">
                <div className="active-page-header-title-container">
                    <h4>Active Queue</h4>
                </div>
                <div className="active-page-header-action-container">
                    <div className="book-page-form-checkbox-button" style={{ display: 'none', alignItems: 'center', marginRight: '16px' }}>
                        <input
                            type="checkbox"
                            id="isArchiveCheckbox"
                            name="isArchiveCheckbox"
                            checked={isArchive}
                            onChange={handleArchiveChange}
                            style={{ cursor: 'pointer', borderRadius: '0.25rem' }}
                        />
                        <label htmlFor="isArchiveCheckbox" style={{ marginLeft: '8px', cursor: 'pointer', fontSize: '14px', color: '#002449' }}>
                            Is Archive Required
                        </label>
                    </div>
                    <div className="active-page-header-button-container">
                        <button className="active-page-header-button" data-track="queue-active-export" onClick={handleExport}>
                            <span>
                                <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 18 18"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_875_15157)">
                                        <path
                                            d="M11.5447 3.04024L11.1433 3.48621L11.1433 3.48621L11.5447 3.04024ZM14.5138 5.71241L14.1124 6.15839L14.5138 5.71241ZM16.2405 7.61557L15.6924 7.85967L15.6924 7.85968L16.2405 7.61557ZM2.37868 15.6213L2.80294 15.1971H2.80294L2.37868 15.6213ZM15.6213 15.6213L15.1971 15.1971L15.6213 15.6213ZM10.5 16.5V15.9H7.5V16.5V17.1H10.5V16.5ZM1.5 10.5H2.1V7.5H1.5H0.9V10.5H1.5ZM16.5 10.1722H15.9V10.5H16.5H17.1V10.1722H16.5ZM11.5447 3.04024L11.1433 3.48621L14.1124 6.15839L14.5138 5.71241L14.9152 5.26643L11.9461 2.59426L11.5447 3.04024ZM16.5 10.1722H17.1C17.1 8.90922 17.1121 8.09787 16.7886 7.37147L16.2405 7.61557L15.6924 7.85968C15.8879 8.29862 15.9 8.80322 15.9 10.1722H16.5ZM14.5138 5.71241L14.1124 6.15839C15.1299 7.07417 15.4969 7.42073 15.6924 7.85967L16.2405 7.61557L16.7886 7.37147C16.4651 6.64507 15.8539 6.11131 14.9152 5.26643L14.5138 5.71241ZM7.52234 1.5V2.1C8.71153 2.1 9.15094 2.10926 9.54196 2.25931L9.75692 1.69914L9.97188 1.13897C9.325 0.890737 8.62066 0.9 7.52234 0.9V1.5ZM11.5447 3.04024L11.9461 2.59426C11.1337 1.8631 10.6187 1.38716 9.97188 1.13897L9.75692 1.69914L9.54196 2.25931C9.93306 2.40939 10.2639 2.69476 11.1433 3.48621L11.5447 3.04024ZM7.5 16.5V15.9C6.06882 15.9 5.0558 15.8987 4.28813 15.7955C3.53772 15.6946 3.11222 15.5063 2.80294 15.1971L2.37868 15.6213L1.95442 16.0456C2.52382 16.615 3.24476 16.866 4.12824 16.9848C4.99445 17.1013 6.10275 17.1 7.5 17.1V16.5ZM1.5 10.5H0.9C0.9 11.8973 0.898726 13.0055 1.01519 13.8718C1.13397 14.7552 1.38501 15.4762 1.95442 16.0456L2.37868 15.6213L2.80294 15.1971C2.49367 14.8878 2.30537 14.4623 2.20448 13.7119C2.10127 12.9442 2.1 11.9312 2.1 10.5H1.5ZM10.5 16.5V17.1C11.8973 17.1 13.0055 17.1013 13.8718 16.9848C14.7552 16.866 15.4762 16.615 16.0456 16.0456L15.6213 15.6213L15.1971 15.1971C14.8878 15.5063 14.4623 15.6946 13.7119 15.7955C12.9442 15.8987 11.9312 15.9 10.5 15.9V16.5ZM16.5 10.5H15.9C15.9 11.9312 15.8987 12.9442 15.7955 13.7119C15.6946 14.4623 15.5063 14.8878 15.1971 15.1971L15.6213 15.6213L16.0456 16.0456C16.615 15.4762 16.866 14.7552 16.9848 13.8718C17.1013 13.0055 17.1 11.8973 17.1 10.5H16.5ZM1.5 7.5H2.1C2.1 6.06882 2.10127 5.0558 2.20448 4.28813C2.30537 3.53772 2.49367 3.11222 2.80294 2.80294L2.37868 2.37868L1.95442 1.95442C1.38501 2.52382 1.13397 3.24476 1.01519 4.12824C0.898726 4.99445 0.9 6.10275 0.9 7.5H1.5ZM7.52234 1.5V0.9C6.11759 0.9 5.0039 0.898736 4.13418 1.01514C3.24765 1.13379 2.52428 1.38455 1.95442 1.95442L2.37868 2.37868L2.80294 2.80294C3.11175 2.49413 3.53856 2.30555 4.29336 2.20453C5.06497 2.10126 6.08378 2.1 7.52234 2.1V1.5Z"
                                            fill="currentColor"
                                        />
                                        <path
                                            opacity="0.5"
                                            d="M9.75 1.875V3.75C9.75 5.51777 9.75 6.40165 10.2992 6.95083C10.8483 7.5 11.7322 7.5 13.5 7.5H16.5"
                                            stroke="currentColor"
                                            strokeWidth="1.2"
                                        />
                                        <path
                                            opacity="0.5"
                                            d="M6.375 10.125L6.375 13.875M6.375 13.875L7.875 12.4688M6.375 13.875L4.875 12.4688"
                                            stroke="currentColor"
                                            strokeWidth="1.2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_875_15157">
                                            <rect width="18" height="18" fill="white" />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </span>
                        </button>
                    </div>
                    <div className="active-page-header-button-container">
                        <button
                            className="active-page-header-add-button"
                            onClick={() => handleNavigation("/intake")}
                        >
                            <span>
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_1193_9975)">
                                        <circle
                                            opacity="0.5"
                                            cx="8.00016"
                                            cy="7.99992"
                                            r="6.66667"
                                            stroke="currentColor"
                                            strokeWidth="1.2"
                                        />
                                        <path
                                            d="M10 8.00002L8 8.00002M8 8.00002L6 8.00002M8 8.00002L8 6M8 8.00002L8 10"
                                            stroke="currentColor"
                                            strokeWidth="1.2"
                                            strokeLinecap="round"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_1193_9975">
                                            <rect width="16" height="16" fill="currentColor" />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </span>Add New
                        </button>
                    </div>
                </div>
            </div>
            <div className="active-page-body-container">
                <div className="rounded-table-wrapper">
                    <Table
                        columns={columns}
                        dataSource={data}
                        className="common-pagination common-grid"
                        pagination={{
                            ...pagination,
                            pageSizeOptions: ["10", "20", "30", "40", "50", "100"],
                            showSizeChanger: true,
                            position: ["bottomRight"],
                            defaultPageSize: 10,
                            showTotal: (total, range) =>
                                renderCommonPaginationTotal(total, range, {
                                    pagination,
                                    handleTableChange,
                                    searchFilters,
                                    sortedInfo
                                }),
                        }}
                        rowKey={(record) => record.bidNo}
                        scroll={{ x: "max-content", y: "calc(100vh - 280px)" }}
                        rowClassName={getRowClassName}
                        onChange={handleTableChange}
                    />
                </div>
            </div>
        </div>
    );
};

Cancelqueue.propTypes = {};

export default Cancelqueue;